/**
 * The content builder's agent contract, shared by its route, its catalog
 * tools and its note (SAL-3310): the run object they all read, the two tool
 * names, how a step is steered, and how the page's unsaved drafts are read
 * back. Nothing here touches the database, so a client component may read
 * the names.
 */
import {
  PROGRAM_SECTION_TYPE_IDS,
  getProgramSectionType,
} from "@/lib/ai-content-builder/program-types";

/** The builder's own catalog tools (lib/agent-tools/content-builder-tool-definitions.js). */
export const READ_WEBSITE_TOOL = "content_builder_readWebsite";
export const GENERATE_DRAFTS_TOOL = "content_builder_generateDrafts";
// Admin-only: a learning path is created through the same code as the
// catalog's salesably_create_learning_path, which admins alone may call.
export const DRAFT_LEARNING_PATH_TOOL = "content_builder_draftLearningPath";
export const CONTENT_BUILDER_TOOLS = Object.freeze([
  READ_WEBSITE_TOOL,
  GENERATE_DRAFTS_TOOL,
  DRAFT_LEARNING_PATH_TOOL,
]);

const MAX_EXISTING_CANDIDATES_PER_TYPE = 25;
const MAX_EXISTING_VALUE_CHARS = 1200;
const MAX_EXISTING_CONTEXT_CHARS = 12000;

export const MAX_CONTEXT_TOOL_STEPS = 2;
export const MAX_DRAFT_TOOL_ATTEMPTS = 2;

/**
 * One content builder run, as the route builds it and the builder's tools
 * and note read it from the tool context (`context.builder`): what the admin
 * asked and attached, what was read for it, the page's unsaved drafts, and
 * what the drafting tool has produced so far. `send` emits the page's
 * stream events.
 */
export function createBuilderTurn({
  runId,
  latestRequest,
  contextState = {},
  existingSections = [],
  preparedSections = [],
  sourceText = "",
  sourceWarnings = [],
  studioBlock = "",
  send = () => {},
}) {
  return {
    runId,
    latestRequest,
    contextState,
    existingSections,
    preparedSections,
    sourceText,
    sourceWarnings,
    studioBlock,
    send,
    // Content read mid-run, shared with every later draft so none refetches.
    dynamicDocuments: [],
    draftedSummaries: [],
    draftErrors: new Map(),
    draftBatch: 0,
    draftAttempts: 0,
  };
}

export function createProgramCandidateId({
  contentType,
  runId,
  batch,
  index,
}) {
  return `${contentType}-${runId}-b${batch}-${index + 1}`;
}

function normalizeDraftValue(value) {
  if (typeof value === "string") {
    return value.slice(0, MAX_EXISTING_VALUE_CHARS);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 12).map(normalizeDraftValue);
  }

  return null;
}

function normalizeDraftCandidate(contentType, candidate) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return null;
  }

  const config = getProgramSectionType(contentType);
  const allowedFields = new Set([
    "id",
    ...(config?.fields || []).map((field) => field.name),
  ]);

  return Object.fromEntries(
    Object.entries(candidate)
      .filter(([key]) => allowedFields.has(key))
      .map(([key, value]) => [key, normalizeDraftValue(value)])
      .filter(([, value]) => value !== null),
  );
}

export function normalizeExistingDraftSections(sections = []) {
  return (Array.isArray(sections) ? sections : [])
    .filter((section) =>
      PROGRAM_SECTION_TYPE_IDS.includes(section?.contentType),
    )
    .map((section) => ({
      contentType: section.contentType,
      candidates: (Array.isArray(section.candidates) ? section.candidates : [])
        .slice(0, MAX_EXISTING_CANDIDATES_PER_TYPE)
        .map((candidate) =>
          normalizeDraftCandidate(section.contentType, candidate),
        )
        .filter(Boolean),
    }))
    .filter((section) => section.candidates.length > 0);
}

export function formatExistingDraftSections(sections = []) {
  const normalizedSections = normalizeExistingDraftSections(sections);
  if (normalizedSections.length === 0) {
    return "";
  }

  const text = JSON.stringify(normalizedSections);
  return text.length > MAX_EXISTING_CONTEXT_CHARS
    ? `${text.slice(0, MAX_EXISTING_CONTEXT_CHARS)}...`
    : text;
}

export function buildRevisionFocus({
  contentType,
  focus,
  operation,
  existingSections,
}) {
  const requestedFocus = typeof focus === "string" ? focus.trim() : "";
  if (operation !== "replace") {
    return requestedFocus;
  }

  const existingSection = normalizeExistingDraftSections(existingSections).find(
    (section) => section.contentType === contentType,
  );
  if (!existingSection) {
    return requestedFocus;
  }

  const revisionContext = JSON.stringify(existingSection.candidates);
  return [
    requestedFocus,
    "Revise the current drafts below. Preserve useful details that the request does not change, and return complete replacement drafts.",
    revisionContext,
  ]
    .filter(Boolean)
    .join("\n\n")
    .slice(0, MAX_EXISTING_CONTEXT_CHARS);
}

export function resolveProgramDraftOperation({
  requestedOperation,
  hasExistingDrafts = false,
  requestText = "",
} = {}) {
  if (!hasExistingDrafts) {
    return "append";
  }

  const normalizedRequest = String(requestText);
  const asksForRevision =
    /\b(revise|rewrite|replace|change|update|modify|edit|fix|shorten|tighten|simplify|reword|remove|delete|instead)\b/i.test(
      normalizedRequest,
    ) ||
    /\bmake\s+(it|them|these|those|the|all)\b/i.test(normalizedRequest) ||
    /\badd\b[^.!?\n]{0,40}\b(detail|context|specificity|evidence|clarity)\b/i.test(
      normalizedRequest,
    );
  if (asksForRevision) {
    return "replace";
  }

  const asksForMore =
    /\b(add|create|draft|generate|give me)\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten|some|few|several|another|additional|extra|new|more)\b/i.test(
      normalizedRequest,
    ) ||
    /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|some|few|several)\s+more\b/i.test(
      normalizedRequest,
    );
  if (asksForMore) {
    return "append";
  }

  if (requestedOperation === "replace") {
    return "replace";
  }

  if (requestedOperation === "append") {
    return "append";
  }

  return "replace";
}

export function getProgramAgentStepControl({
  stepNumber = 0,
  hasDrafts = false,
  draftAttempts = 0,
} = {}) {
  if (hasDrafts) {
    return {};
  }

  if (draftAttempts >= MAX_DRAFT_TOOL_ATTEMPTS) {
    return { toolChoice: "none" };
  }

  if (stepNumber >= MAX_CONTEXT_TOOL_STEPS) {
    return {
      activeTools: [GENERATE_DRAFTS_TOOL],
      toolChoice: { type: "tool", toolName: GENERATE_DRAFTS_TOOL },
    };
  }

  return { toolChoice: "required" };
}
